'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { delocalizePath, localizePathname } from '@/lib/i18n-routes';


/**
 * Hook that derives the current locale and internal pathname from the URL.
 * This is the single source of truth for "what locale are we in".
 *
 * usePathname() returns the browser URL (e.g. "/de/suche") since middleware
 * only rewrites internally. We parse it to extract locale + internal path.
 */
export function useLocale() {
  const pathname = usePathname();

  return useMemo(() => {
    const { locale, pathname: internalPathname } = delocalizePath(pathname);
    return {
      locale,
      /** The internal (filesystem) pathname, e.g. "/search", "/industry/62/slug" */
      internalPathname,
    };
  }, [pathname]);
}

/**
 * Hook that returns a function to build localized paths for navigation.
 * Usage: const localePath = useLocalePath();
 *        localePath('/search') -> '/de/suche'
 */
export function useLocalePath() {
  const { locale } = useLocale();

  return useMemo(() => {
    return (internalPath: string) => localizePathname(internalPath, locale);
  }, [locale]);
}
